import { DEFAULT_LOCALE } from '@/constants/locale.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import useCategoryFilters from '@/hooks/useCategoryFilters.js';
import React, { useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Cookies from 'universal-cookie';
import { isEmpty, omit } from 'lodash';
import cn from 'classnames';
import { WaveBreadCrumb, HomePageBanner, FilterChipsList, SortingAndFilters } from '@desquared/wave-components-library';
import {
  getProductInfo,
  selectPromotion,
  viewItemList,
  viewPromotion,
  bannersAnalyticsFunction,
} from '@/utils/analytics.js';

import CategoryCatalogue from '@/components/CategoryCatalogue/CategoryCatalogue';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import FiltersFAB from '@/components/FiltersFAB/FiltersFAB';
import PageLayout from '@/components/PageLayout';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import CategoriesMenu from '@/components/CategoriesMenu/CategoriesMenu';
import FiltersAside from '@/components/FiltersAside/FiltersAside';
import User from '@/store/user';
import CartStore from '@/store/cart';

import { getCmsHomepageLayout } from '@/api/cms';
import { getV31Products } from '@/api/Products';
import { wave } from '@/api/config';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import { getLayoutProps } from '@/utils/layout/layout';
import { getCategoryPathsFromFile } from '@/utils/staticPaths';
import { getCategoriesFromFile } from '@/utils/getCategories';
import { getOrFetchOrderLimitations } from '@/utils/orderLimitations';
import {
  getProductsWithCategory,
  getCategoryPathArrayFromSlug,
  sortByPosition,
  getProductsWithFixedCategory,
  getSortedSubcategoriesFromTree,
} from '@/utils/category';
import { trimmCategories } from '@/utils/trimmCategories';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { setSideBanner } from '@/utils/bannerUtils/bannerUtils';
import { setBanner } from '@/utils/homePageCmsCall';
import useProductTags from '@/hooks/useProductTags';
import { SORT_OPTIONS } from '@/constants/sort';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { useInfiniteQuery } from '@tanstack/react-query';

import styles from '@/styles/Categories.module.css';
import { useTranslations } from 'next-intl';

const cookies = new Cookies();
const authorization = cookies.get('token');

export default function Categories({
  categoriesFromFile,
  selectedCategoryTree,
  responsiveLogo,
  subCategories,
  privateApiKey,
  imageBanners,
}) {
  const router = useRouter();
  const t = useTranslations();
  const isMobile = useWindowUnderThreshold(850);
  const { slug } = router.query;

  const [selectedSort, setSelectedSort] = useState(0);

  const [limitations, setLimitations] = useState([]);
  const { productTags } = useProductTags();
  const selectedCategoryId = selectedCategoryTree[selectedCategoryTree.length - 1]._id;
  const { normalCategories, featuredCategories } = trimmCategories(
    categoriesFromFile,
    t('category_details.all_categories')
  );
  const sortedNormalCategories = sortByPosition(normalCategories);
  const sortedFeaturedCategories = sortByPosition(featuredCategories);
  const allCategories = [...sortedFeaturedCategories, ...sortedNormalCategories];
  const isSecondLevelCategory = router.query.slug.length === 1;

  const {
    getCategoryFilters,
    isLoading: isFiltersLoading,
    attributes,
    brands,
    categoryKinds,
    tags,
    selectedFilters,
    handleSelectFilter,
    numberOfSelectedFilters,
    clearFilters,
    handleApplyFilters,
    getFiltersFromUrl,
    offerFiltering,
  } = useCategoryFilters(router.query.subCategory ? router.query.subCategory : selectedCategoryId, {
    allowAttributes: true,
    allowBrands: true,
    allowCategoryKinds: true,
    allowTags: true,
  });
  const { bundleOffersSummary } = useBundleOffersSummary();
  const [filterChipsItems, setFilterChipsItems] = useState([]);
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false);

  useEffect(() => {
    const isCartVisible$ = CartStore.getIsVisble$().subscribe(setIsCartVisible);
    return () => {
      isCartVisible$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isFiltersSheetOpen) {
      getCategoryFilters();
    }
  }, [isFiltersSheetOpen]);

  const { getCollectionTypes, selectedService } = useContext(DeliveryServicesContext);

  useEffect(() => {
    // setIsFiltersSheetOpen(undefined);
    // clearFilters();
    setSelectedSort(0);

    //eslint-disable-next-line
  }, [selectedCategoryId]);

  const breadCrumbItems = selectedCategoryTree.map(i => {
    return {
      title: i.name,
      link: `/${router.defaultLocale === router.locale ? '' : router.locale + '/'}` + i.fullSlug,
    };
  });

  const currentCategory = selectedCategoryTree[selectedCategoryTree.length - 1];
  const categorySearchType = router.query.subCategory ? 'SAME_LEVEL' : 'ALL_LEVELS';

  const handleSelectSubcategory = async item => {
    const url = { pathname: router.pathname };
    if (item.text === t('category_details.all_categories')) {
      url['query'] = omit({ ...router.query }, 'subCategory');
    } else if (item.selected) {
      url['query'] = omit({ ...router.query }, 'subCategory');
    } else {
      url['query'] = { ...router.query, subCategory: item._id };
    }
    router.push(url, undefined, { shallow: true });
  };

  const fetchPageForCategoryProducts = async ({ pageParam }) => {
    const { filtersWithIds, offerFilters } = getFiltersFromUrl();
    const newProducts = await getV31Products(
      {
        pageNumber: pageParam.toString(),
        category: router.query.subCategory
          ? router.query.subCategory
          : selectedCategoryTree[selectedCategoryTree.length - 1]._id,
        sort: [SORT_OPTIONS[selectedSort]],
        collectionTypes: getCollectionTypes(),
        categorySearchType,
        ...filtersWithIds,
        ...offerFilters,
      },
      router.locale || DEFAULT_LOCALE,
      privateApiKey
    );

    return newProducts;
  };

  useEffect(() => {
    if (router.isReady) {
      setFilterChipsItems([
        { text: t('category_details.all_categories'), selected: !router.query.subCategory },
        ...sortByPosition(
          subCategories.map(i => {
            return { text: i.name, ...i, selected: router.query.subCategory === i._id };
          })
        ),
      ]);
    }
  }, [router.query, router.isReady, setFilterChipsItems, t, subCategories]);

  // TODO: utilize error state
  const {
    data: products,
    error,
    fetchNextPage,
    isLoading: isLoadingProducts,
    isFetchingNextPage: isFetchingProductsNextPage,
  } = useInfiniteQuery({
    queryKey: ['category/[...slug]', router.query, selectedSort, router.locale],
    queryFn: fetchPageForCategoryProducts,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    select: data => {
      const products = [];

      data.pages.forEach(page => {
        page.forEach(product => {
          products.push(enhanceProductWithSlug(product));
        });
      });

      return products;
    },
    enabled: !!selectedService?.id,
    // Manages how long before data needs refetching on back navigation, page refocus e.t.c - set to 1 minute
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (products?.length > 0) {
      viewItemList(
        products.map((i, idx) => getProductInfo(i, { index: idx + 1 }, router.query.slug[0])),
        {
          item_list_id: 'category',
          item_list_name: selectedCategoryTree[selectedCategoryTree.length - 1]?.name || '',
        }
      );
    }
  }, [products?.length]);

  useEffect(() => {
    const user$ = User.subscribe(_user => {
      async function getNewLists() {}
      if (!isEmpty(authorization)) getNewLists(authorization);
    });
    async function getLimitations() {
      const limitation = await getOrFetchOrderLimitations();
      setLimitations(limitation);
    }
    getLimitations();
    return () => user$.unsubscribe();
  }, []);

  useEffect(() => {
    if (!slug[1] && imageBanners?.length > 0) {
      viewPromotion({
        creative_name: `category_banner_${selectedCategoryId}`,
        creative_slot: 'category_banner',
        promotion_id: `category_banner_${selectedCategoryId}`,
        promotion_name: `category_banner_${currentCategory.name}`,
      });
    }
  }, [router.asPath]);

  const hasSubCategories = selectedCategoryTree?.[0]?.subCategories?.length;

  let bannerVariable = null;

  if (imageBanners.hasOwnProperty(slug[slug.length - 1])) {
    bannerVariable = imageBanners[slug[slug.length - 1]];
  }

  return (
    <>
      <Head>
        <title>
          {process.env.displayName} | {currentCategory.name}
        </title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content={currentCategory.description || ''} />
        <meta property="og:title" content={`${process.env.appName} | ${currentCategory.name}`} />
        <meta property="og:description" content={currentCategory.description || ''} />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer}>
        {isMobile ? (
          <div className={styles.breadCrumbContainer}>
            <WaveBreadCrumb
              LinkComponent={Link}
              items={breadCrumbItems}
              iconLink={`/${router.locale === router.defaultLocale ? '' : router.locale}`}
            />
          </div>
        ) : null}
        <div className={styles.categoriesWrapper}>
          <div className={styles.categoriesContainer}>
            <div className={styles.categoriesInnerContainer}>
              <CategoriesMenu
                categories={allCategories}
                slug={slug}
                featuredAmmount={featuredCategories.length}
                selectedCategoryTree={selectedCategoryTree}
              />
            </div>
          </div>
          <div className={styles.slugProductsContainer}>
            {isMobile ? null : (
              <div className={styles.breadCrumbContainer}>
                <WaveBreadCrumb
                  items={breadCrumbItems}
                  LinkComponent={Link}
                  iconLink={`/${router.locale === router.defaultLocale ? '' : router.locale}`}
                />
              </div>
            )}

            {isMobile ? null : (
              <div className={styles.titleContainer}>
                <CategoryTitle
                  title={currentCategory?.name}
                  subtitle={t('category_details.subtitle', { categoryTitle: currentCategory?.name })}
                />
                <SortingAndFilters
                  text={t('category_details.sorting_filters.btn_title')}
                  numberOfFilters={numberOfSelectedFilters || undefined}
                  onClick={() => {
                    setIsFiltersSheetOpen(true);
                  }}
                />
              </div>
            )}

            {isMobile ? (
              <>
                <ScrollToTop />

                <FiltersFAB
                  onClick={() => {
                    setIsFiltersSheetOpen(true);
                  }}
                />
              </>
            ) : null}
            <ScrollToTop />
            {filterChipsItems.length > 1 && (
              <div className={styles.chipsContainer}>
                <FilterChipsList items={filterChipsItems} onChange={handleSelectSubcategory} />
              </div>
            )}
            {bannerVariable && (
              <HomePageBanner
                analyticsFunction={bannersAnalyticsFunction}
                isFixedWidth={true}
                images={bannerVariable}
                refreshTime={1000000}
                isMobile={isMobile}
                showPagination={true}
                classOption={cn(styles.banner, styles.slugbanner)}
                type={bannerVariable?.length > 1 ? 'loop' : 'slide'}
                LinkComponent={Link}
                onClick={index => {
                  selectPromotion({
                    creative_name: bannerVariable?.[index]?.altText ?? `category_banner_${currentCategory.name}`,
                    creative_slot: bannerVariable?.[index]?.altText ?? `category_banner_${currentCategory.name}`,
                    promotion_id: bannerVariable?.[index]?.altText ?? `category_banner_${currentCategory.name}`,
                    promotion_name: bannerVariable?.[index]?.altText ?? `category_banner_${currentCategory.name}`,
                  });
                }}
              />
            )}
            <CategoryCatalogue
              isLoading={isLoadingProducts || !selectedService?.id}
              isFetchingNextPage={isFetchingProductsNextPage}
              handleLoadMore={fetchNextPage}
              products={
                router.query.subCategory
                  ? getProductsWithFixedCategory(
                      products,
                      categoriesFromFile,
                      productTags,
                      bundleOffersSummary,
                      router.locale,
                      subCategories.find(i => i._id === router.query.subCategory).name
                    )
                  : getProductsWithCategory(
                      products,
                      !isSecondLevelCategory
                        ? filterChipsItems.length > 1
                          ? filterChipsItems
                              .filter(i => i._id)
                              .map(i => {
                                return { _id: i._id, name: i.name };
                              })
                          : getSortedSubcategoriesFromTree(selectedCategoryTree[0])
                        : getSortedSubcategoriesFromTree(selectedCategoryTree[0]),
                      productTags,
                      bundleOffersSummary,
                      router.locale,
                      { showSecondLevelCategory: filterChipsItems.length === 1 }
                    )
              }
              limitations={limitations}
              side_banner={setSideBanner(currentCategory?.side_banner, 'image')}
              side_banner_link={setSideBanner(currentCategory?.side_banner, 'link')}
              bundleOffersSummary={bundleOffersSummary}
              currentCategory={currentCategory}
              hasSubCategories={hasSubCategories}
            />
          </div>
        </div>
      </main>
      {isFiltersSheetOpen && (
        <FiltersAside
          isOpen={isFiltersSheetOpen}
          isLoading={isFiltersLoading}
          handleApplyFilters={handleApplyFilters}
          clearFilters={clearFilters}
          attributes={attributes}
          brands={brands}
          categoryKinds={categoryKinds}
          tags={tags}
          handleClose={() => setIsFiltersSheetOpen(false)}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleSelectFilter}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
          hasClosest={false}
          hideOffers={process.env.hideOffers}
        />
      )}
    </>
  );
}

Categories.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticPaths({ locales }) {
  const allCategoryPaths = getCategoryPathsFromFile();
  const paths = allCategoryPaths
    .filter(i => i)
    .map(path => {
      const pathArr = path.split('/');
      pathArr.shift();
      return {
        params: { slug: pathArr },
      };
    });
  let localizedPaths = [];
  for (const locale of locales) {
    localizedPaths = [...localizedPaths, ...paths.map(path => ({ ...path, locale }))];
  }

  return { paths: localizedPaths, fallback: false };
}

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);
  const categoriesFromFile = await getCategoriesFromFile(context.locale);
  const selectedCategoryInfo = getCategoryPathArrayFromSlug(categoriesFromFile, context.params.slug);
  const selectedCategoryTree = selectedCategoryInfo.categories;

  // Create a new object to store image banners associated with their slugs
  let imageBannersObject = {};

  // Helper function to get banners based on carousel_id and slug
  async function fetchBannersForCategory(category) {
    if (!category.carousel_id) return;

    const { data: BannerData } = await getCmsHomepageLayout(category.carousel_id, context.locale);

    if (BannerData.included?.homepage_banner_web_item?.length > 0) {
      imageBannersObject[category.slug] = setBanner(BannerData.included.homepage_banner_web_item);
    }
  }

  // Fetch image banners for the main category
  await fetchBannersForCategory(selectedCategoryTree[0]);

  // Fetch image banners for each subcategory of the selected category
  if (selectedCategoryTree[0].subCategories?.length > 0) {
    await Promise.all(
      selectedCategoryTree[0].subCategories.map(async subcategory => {
        await fetchBannersForCategory(subcategory);
      })
    );
  }

  // Fetch products as before
  async function getV31ProductsWithAPIKey(resource) {
    const response = await wave.get('/v3.1/products', {
      headers: { 'x-api-key': process.env.PRIVATE_API_KEY, 'Accept-Language': context.locale || DEFAULT_LOCALE },
      params: {
        available: true,
        collectionTypes: [process.env.defaultCollectionType],
        pageNumber: '0',
        pageSize: 30,
        ...resource,
      },
    });

    return response.data;
  }

  let initialProducts = [];

  try {
    initialProducts = await getV31ProductsWithAPIKey({
      category: selectedCategoryTree[selectedCategoryTree.length - 1]._id,
      sort: 'position|asc',
      categorySearchType: 'SAME_LEVEL',
    });
  } catch (err) {
    console.error('>>>getStaticProps initialProducts error');
  }

  // Import the i18n messages
  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    category_details: (await import(`../../../prebuild-data/i18n/${context.locale}/category_details.json`)).default,
    offers: (await import(`../../../prebuild-data/i18n/${context.locale}/offers.json`)).default,
  };

  return {
    props: {
      ...layoutProps,
      categoriesFromFile,
      selectedCategoryTree: selectedCategoryInfo.categories,
      subCategories: selectedCategoryInfo.subCategories,
      initialProducts: initialProducts.map(enhanceProductWithSlug),
      privateApiKey: process.env.NODE_ENV === 'development' ? process.env.PRIVATE_API_KEY : null,
      imageBanners: imageBannersObject, // Now an object mapping slugs to image banners
      messages,
    },
  };
}
